import React, { forwardRef, Ref, useRef } from "react";

import { Controller } from "react-hook-form";

import Image, { ICONS } from "../Image";

import { StyledErrorLabel, StyledLabel } from "../Flex";

import {
  getPatternType,
  input,
  output,
  TextFieldProps,
} from "./TextField.constant";
import {
  StyledEyeIcon,
  StyledEyeIconContainer,
  StyledIconContainer,
  StyledInnerContainer,
  StyledOuterContainer,
  StyledText,
  StyledTextFieldContainer,
  StyleImageContainer,
} from "./TextField.styles";

const TextField = forwardRef(
  (
    {
      label,
      name,
      error,
      control,
      variant = "standart",
      onChange,
      value,
      iconOpen = true,
      disabled,
      onPaste,
      onBlur,
      type,
      defaultValue,
      maxLength,
      onKeyDown,
      tooltipInfo,
      onError,
      textFieldImageSrc = tooltipInfo ? ICONS.TOOLTIP : "",
      placeholder,
    }: TextFieldProps,
    ref?: Ref<HTMLInputElement>
  ) => {
    const refOuter = useRef<HTMLDivElement>(null);

    return control ? (
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        onFocus={() => {
          onError &&
            refOuter?.current?.offsetTop &&
            onError(refOuter?.current?.offsetTop - 20);
        }}
        render={({ onChange: onControlledChange, value: controlledValue }) => {
          return (
            <StyledOuterContainer disabled={disabled} ref={refOuter}>
              <StyledTextFieldContainer>
                <StyledInnerContainer
                  error={error}
                  label={label}
                  type={type}
                  value={controlledValue}
                >
                  {iconOpen && type !== "masked" && error && (
                    <StyledIconContainer>
                      <Image
                        alt='Error Icon'
                        height='24px'
                        srcSet={ICONS.ERROR}
                        width='24px'
                      />
                    </StyledIconContainer>
                  )}

                  {type === "masked" && (
                    <StyledEyeIconContainer>
                      <StyledEyeIcon />
                    </StyledEyeIconContainer>
                  )}

                  <StyledText
                    className='password'
                    defaultValue={defaultValue}
                    disabled={disabled}
                    error={error}
                    maxLength={maxLength}
                    onBlur={onBlur}
                    onChange={(e) =>
                      onControlledChange(output(e.target.value, type))
                    }
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    pattern={getPatternType(type)}
                    type={type}
                    value={input(controlledValue, type)}
                    variant={variant}
                    placeholder={placeholder}
                  />

                  <StyledLabel>{label}</StyledLabel>
                </StyledInnerContainer>

                {variant === "standart" && (
                  <StyledErrorLabel>{error}</StyledErrorLabel>
                )}
              </StyledTextFieldContainer>

              {textFieldImageSrc && (
                <StyleImageContainer>
                  <Image
                    alt='Tooltip Icon'
                    height='20px'
                    srcSet={textFieldImageSrc}
                    tooltipInfo={tooltipInfo}
                    width='20px'
                  />
                </StyleImageContainer>
              )}
            </StyledOuterContainer>
          );
        }}
      />
    ) : (
      <StyledOuterContainer>
        <StyledTextFieldContainer>
          <StyledInnerContainer error={error} label={label} value={value}>
            {error && iconOpen && (
              <StyledIconContainer>
                <Image
                  alt='Error Icon'
                  height='24px'
                  srcSet={ICONS.ERROR}
                  width='24px'
                />
              </StyledIconContainer>
            )}

            <StyledText
              autoComplete='off'
              className='password'
              defaultValue={defaultValue}
              disabled={disabled}
              error={error}
              maxLength={maxLength}
              name={name}
              onChange={(event) => onChange && onChange(event)}
              ref={ref}
              value={value}
              variant={variant}
            />

            {!!label?.length && <StyledLabel>{label}</StyledLabel>}
          </StyledInnerContainer>

          {variant === "standart" && (
            <StyledErrorLabel>{error}</StyledErrorLabel>
          )}
        </StyledTextFieldContainer>
        {textFieldImageSrc && (
          <StyleImageContainer>
            <Image
              alt='Tooltip Icon'
              height='20px'
              srcSet={textFieldImageSrc}
              tooltipInfo={tooltipInfo}
              width='20px'
            />
          </StyleImageContainer>
        )}
      </StyledOuterContainer>
    );
  }
);

export default TextField;
